import {
  FilterKeys,
  LastUpdatedList,
  OperatorType,
  SelfCertificateMapped,
} from "constants/filters";

// types
import { QueryParams } from "types/common.types";
import { Filters, Generic, SubFilter } from "types/filters-store.types";
import { BusinessItem } from "types/results-panel.types";

// data
import { stateAndTerritories } from "data";

// fetch
import { getResults } from "data/fetch";

// helper
import { getSBACertsLabel, getSelfCertsLabel } from "./helper";

export const formattedStatesList = stateAndTerritories.map((item) => ({
  label: `${item.split(" - ")[1]} (${item.split(" - ")[0]})`,
  value: item,
}));

export const formattedStatesListWithoutAbbreviation = stateAndTerritories.map(
  (item) => ({
    label: item.split(" - ")[1],
    value: item,
  })
);

export function getStateAbbreviation(state: string) {
  const stateAbbreviation = stateAndTerritories.find(
    (item) => item.split(" - ")[1] === state
  );

  if (!stateAbbreviation) {
    return state;
  }

  return stateAbbreviation.split(" - ")[0];
}

export function getStateName(stateAbbr: string) {
  const stateName = stateAndTerritories.find(
    (item) => item.split(" - ")[0] === stateAbbr
  );

  if (!stateName) {
    return null;
  }

  return stateName.split(" - ")[1];
}

export function getHighlightedText(
  list: Generic[],
  searchTerm: string,
  highlightValue: boolean = false
) {
  const trimmedSearchTerm = searchTerm.trim();
  if (!trimmedSearchTerm) {
    return list;
  }

  const escapedSearchTerm = searchTerm.replace(
    /[-\/\\^$*+?.()|[\]{}]/g,
    "\\$&"
  );

  const regex = new RegExp(`(${escapedSearchTerm})`, "i");

  const highlightedText = list.map((item) => {
    const highlightedLabel = item.label.replace(regex, `<strong>$&</strong>`);
    let highlightedValue = item.value ?? "";

    if (highlightValue) {
      highlightedValue = item.value.replace(regex, `<strong>$&</strong>`);
    }

    return {
      ...item,
      label: highlightedLabel,
      value: highlightedValue,
    };
  });

  return highlightedText;
}

export function getFilterCount(filters: Filters, filterName: string) {
  switch (filterName) {
    case FilterKeys.LOCATION: {
      return (
        filters.location.states.length +
        filters.location.zipCodes.length +
        filters.location.counties.length +
        filters.location.districts.length +
        filters.location.msas.length
      );
    }

    case FilterKeys.SEARCH_TERM: {
      return filters.searchProfiles.searchTerm?.length > 0 ? 1 : 0;
    }

    case FilterKeys.SBA_CERTIFICATIONS: {
      return filters.sbaCertifications.activeCerts.length;
    }

    case FilterKeys.NAICS: {
      return filters.naics.codes.length;
    }

    case FilterKeys.SELF_CERTIFICATIONS: {
      return filters.selfCertifications.certifications.length;
    }

    case FilterKeys.KEYWORDS: {
      return filters.keywords.list.length;
    }

    case FilterKeys.LAST_UPDATED: {
      const lastUpdatedValue = filters.lastUpdated.date.value;
      const lastUpdatedLabel = filters.lastUpdated.date.label;
    

      if (lastUpdatedValue === "anytime") {
        return 0;
      } else if (lastUpdatedLabel === "Custom") {
        return isValidDateFilterFormat(lastUpdatedValue) ? 1 : 0;
      }

      // if lastUpdatedValue is not "anytime" or "custom"
      return 1;
    }

    case FilterKeys.SAM_STATUS: {
      return filters.samStatus.isActiveSAM ? 1 : 0;
    }

    case FilterKeys.QUALITY_ASSURANCE_STANDARDS: {
      return filters.qualityAssuranceStandards.qas.length;
    }

    case FilterKeys.BONDING_LEVELS: {
      return Object.values(filters.bondingLevels).filter((val) => val).length;
    }

    case FilterKeys.BUSINESS_SIZE: {
      return filters.businessSize.numberOfEmployees !== "" ? 1 : 0;
    }

    case FilterKeys.ANNUAL_REVENUE: {
      return filters.annualRevenue.annualGrossRevenue !== "" ? 1 : 0;
    }

    case FilterKeys.ENTITY_DETAIL_ID: {
      return filters.entityDetailId ? 1 : 0;
    }

    default:
      return 0;
  }
}

function isValidDateFilterFormat(dateString: string) {
  if (dateString === "-") { // front end will send "-" if a value was input, then removed
    return false;
  }

  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  const dates = dateString.split("-");

  // if any of the dates are empty, return false
  if (dates.includes("")) {
    return false;
  }

  // const dateArr = dates.filter((date) => date !== "");
  return dates.reduce((acc, date) => dateRegex.test(date) && acc, true);
}

export const refineFilterOptions = (filters: Filters) => {
  const filterCategories = Object.keys(filters) as Array<keyof Filters>;

  const appliedFilterCategories = filterCategories
    .filter((category) => getFilterCount(filters, category) > 0)
    .map((category) => category);

  const queryParams: QueryParams[] = [];

  appliedFilterCategories.forEach((category) => {
    switch (category) {
      case FilterKeys.SEARCH_TERM:
        queryParams.push({
          category,
          options: [filters.searchProfiles.searchTerm],
        });
        break;

      case FilterKeys.LOCATION: {
        const states = filters.location.states.map((state) =>
          state.value.slice(0, 2)
        );

        const zipCodes = filters.location.zipCodes.map(
          (zip) => `z-${zip.value}`
        );

        const counties = filters.location.counties.map((county) => {
          const [_, stateName, fipCode] = county.value.split("-");
          const stateAbbr = getStateAbbreviation(stateName);
          return `c-${stateAbbr}-${fipCode}`;
        });

        const districts = filters.location.districts.map((district) => {
          const [stateName, fipCode] = district.value.split("-");
          const stateAbbr = getStateAbbreviation(stateName);
          return `d-${stateAbbr}-${fipCode}`;
        });

        const msas = filters.location.msas.map((msa) => {
          return `m-${msa.value}-${msa.label}`;
        });

        const options = [...states, ...zipCodes, ...counties, ...districts, ...msas];

        queryParams.push({
          category,
          options: options,
        });
        break;
      }

      case FilterKeys.SBA_CERTIFICATIONS:
        queryParams.push({
          category,
          options: filters.sbaCertifications.activeCerts.map(
            (cert) => cert.value
          ),
          operator: filters.sbaCertifications.operatorType.toUpperCase(),
          isPrevious: filters.sbaCertifications.isPreviousCert,
        });
        break;

      case FilterKeys.NAICS:
        queryParams.push({
          category,
          options: filters.naics.codes.map((code) => code.value),
          operator: filters.naics.operatorType.toUpperCase(),
          isPrimary: filters.naics.isPrimary,
        });
        break;

      case FilterKeys.KEYWORDS:
        queryParams.push({
          category,
          options: filters.keywords.list.map((keyword) => keyword.value),
          operator: filters.keywords.operatorType.toUpperCase(),
        });
        break;

      case FilterKeys.SELF_CERTIFICATIONS:
        queryParams.push({
          category,
          options: filters.selfCertifications.certifications.map(
            (cert) => cert.value
          ),
          operator: filters.selfCertifications.operatorType.toUpperCase(),
        });
        break;

      case FilterKeys.LAST_UPDATED:
        queryParams.push({
          category,
          options: [filters.lastUpdated.date.value],
        });
        break;

      case FilterKeys.BUSINESS_SIZE: {
        const numberOfEmployees = filters.businessSize.numberOfEmployees;
        const relationOperator = filters.businessSize.relationOperator;

        const options = {
          relationOperator,
          numberOfEmployees,
        };

        queryParams.push({
          category,
          options: options as any,
        });
        break;
      }

      case FilterKeys.ANNUAL_REVENUE: {
        const annualGrossRevenue = filters.annualRevenue.annualGrossRevenue;
        const relationOperator = filters.annualRevenue.relationOperator;

        const options = {
          relationOperator,
          annualGrossRevenue,
        };

        queryParams.push({
          category,
          options: options as any,
        });
        break;
      }

      case FilterKeys.SAM_STATUS:
        queryParams.push({
          category,
          options: filters.samStatus.isActiveSAM && ["active"],
        });
        break;

      case FilterKeys.QUALITY_ASSURANCE_STANDARDS:
        queryParams.push({
          category,
          options: filters.qualityAssuranceStandards.qas.map(
            (qas) => qas.value
          ),
        });
        break;

      case FilterKeys.BONDING_LEVELS: {
        const bondingLevels = filters.bondingLevels;

        const options = {
          construction: {
            individual: bondingLevels.constructionIndividual || null,
            aggregate: bondingLevels.constructionAggregate || null,
          },
          service: {
            individual: bondingLevels.serviceIndividual || null,
            aggregate: bondingLevels.serviceAggregate || null,
          },
        };

        queryParams.push({
          category,
          options: options as any,
        });
        break;
      }
    }
  });

  // if(filters?.location?.searchTerm){
  //   queryParams.push({
  //     category:"searchBusiness",
  //     options:[filters?.location?.searchTerm],
  //   });
  // }

  return queryParams;
};

export const fetchAllFilterData = async (query: QueryParams[]) => {
  try {
    // fetch initial 25 results.
    const fetchInitialData = await getResults({
      queryKey: [query],
      pageSize: 25,
    });

    // check if results are more than 25
    if (fetchInitialData.total > 25) {
      // fetch all results
      const fetchAllData = await getResults({
        queryKey: [query],
        pageSize: fetchInitialData.total,
      });

      // merge results
      return {
        // results: [
        //   ...fetchInitialData.results,
        //   ...fetchAllData.results.slice(25),
        // ],
        results: fetchAllData.results,
        total: fetchAllData.total,
        totalPages: fetchAllData.totalPages,
      };
    } else {
      return {
        results: fetchInitialData.results,
        total: fetchInitialData.total,
        totalPages: fetchInitialData.totalPages,
      };
    }
  } catch (error: any) {
    throw new Error(error.message);
  }
};